import { useQuery } from '@apollo/client/react';
import { capitalize } from 'lodash-es';
import { useEffect } from 'react';

import { ConnectionStatus } from '@pflegia/types/common/enums/connection-status.enum';
import { OfferType } from '@pflegia/types/common/enums/offer-type.enum';
import { CandidateActions } from '@pflegia/types/common/enums/post-back/candidate-actions.enum';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import JobPositionIdContextProvider from '@pflegia/ui/contexts/providers/JobPositionIdContextProvider';
import { HOME, JOB_BOARD, JOB_DETAILS, JOB_LINK, JOBS } from '@pflegia/ui/pflegia/constants/routes';
import useJobTitle from '@pflegia/ui/shared/job-title/hooks/use-job-title';
import { removeTrailingSlash } from '@pflegia/util/common/remove-trailing-slash';
import { getJobPositionPathname } from '@pflegia/util/functions/job-position/helpers';

import getCanonicalForTransparentPages from '@domains/job-board/utils/get-canonical-for-transparent-pages';
import JobDetail from '@domains/job-detail';
import { JobDetailPageProps } from '@domains/job-detail/types/job-detail-page-props';
import { useWhatsAppOptInContext } from '@domains/whatsapp-opt-in/WhatsAppOptInContext';

import { useParseJobPositionForPerks } from '@hooks/get-job-position.hook';

import { TrackingEventName } from '@shared/application/marketing/events/tracking-event-name';
import { useTracking } from '@shared/application/tracking.service';
import Breadcrumb from '@shared/components/breadcrumb/Breadcrumb';
import { FIND_MATCH_FOR_JOB_POSITION } from '@shared/graphql/operations/find-match-for-job-position';
import { useRouterHelper } from '@shared/hooks/use-router-helper';
import { useTrackUtm } from '@shared/hooks/use-track-utm';

import JobDetailsJobPostingJsonLd from '../meta/job-details-job-posting-json-ld';
import JobDetailsSeoTags from '../meta/job-details-seo-tags';

const TIMEOUT_FOR_TRACKING_EVENT_IN_MS = 500;

const JobDetailBase: React.FC<JobDetailPageProps> = ({
  alternativeJobTitlesByPriority,
  isAnonymized = true,
  jobPosition,
  matchReasonsIsTest,
  nearbyCities,
  relatedJobPositions,
  validThrough,
}) => {
  const { track } = useTracking();
  const { trackUtm } = useTrackUtm();
  const { addViewedJobPosition } = useWhatsAppOptInContext();
  // @ts-expect-error Conflict with custom vs generated type
  const { perks, highlights } = useParseJobPositionForPerks(jobPosition);
  const authenticated = useAuth();
  const { isTransparentJobDetails } = useRouterHelper();
  const { translateJobTitle } = useJobTitle();
  const city = jobPosition?.geoLocation?.city;
  const jobTitle = jobPosition?.jobTitles?.[0]
    ? translateJobTitle({
        title: jobPosition.jobTitles[0].title,
        deputy: jobPosition.jobTitles[0].deputy ?? false,
      })
    : '';

  const basePath = isTransparentJobDetails ? JOB_DETAILS : JOB_LINK;
  const jobDetailsUrl = getJobPositionPathname(
    { id: jobPosition.id, title: jobPosition.title, geoLocation: jobPosition.geoLocation },
    { basePath },
  );
  const positionLink = getCanonicalForTransparentPages(jobDetailsUrl, basePath);
  const query = new URLSearchParams();
  if (city) query.set('standort', city.toLowerCase());
  if (jobTitle) query.set('position', jobTitle.toLowerCase());

  const jobListingsBase = isTransparentJobDetails ? JOBS : JOB_BOARD;
  const jobListingsUrl = query.toString() ? `${jobListingsBase}?${query.toString()}` : jobListingsBase;

  const breadcrumbItems = [
    {
      label: 'Startseite',
      href: HOME,
    },
    {
      label: city ? `Stellenangebote in ${capitalize(city)}` : 'Stellenangebote',
      href: jobListingsBase + (city ? `?standort=${city.toLowerCase()}` : ''),
    },
    ...(jobTitle
      ? [
          {
            label: city ? `${jobTitle} Jobs in ${capitalize(city)}` : `${jobTitle} Jobs`,
            href: jobListingsUrl,
          },
        ]
      : []),
    {
      label: jobPosition.title,
      jsonIdHref: removeTrailingSlash(positionLink.split('?')[0]),
    },
  ];
  const shouldAddBreacrumbJsonIdSchema = Boolean(positionLink) && !positionLink?.startsWith(`${JOB_LINK}/`);

  useEffect(() => {
    trackUtm(CandidateActions.Clicked);
  }, [trackUtm]);

  useEffect(() => {
    addViewedJobPosition(jobPosition.id);
  }, [addViewedJobPosition, jobPosition.id]);

  // Fetch candidate-specific match details for accurate invite/application UI state.
  const { data, loading: matchLoading } = useQuery(FIND_MATCH_FOR_JOB_POSITION, {
    skip: !authenticated,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    variables: { jobPositionId: jobPosition.id },
  });
  useEffect(() => {
    if (!data?.findMatchForJobPosition) return;
    const { findMatchForJobPosition } = data;
    setTimeout(() => {
      if (findMatchForJobPosition.connectionStatus === ConnectionStatus.Pending && findMatchForJobPosition.offerType === OfferType.Push) {
        track(
          findMatchForJobPosition.isMachineLearningInvite ? TrackingEventName.ModelPushInviteOpen : TrackingEventName.RegularPushInviteOpen,
        );
      }
    }, TIMEOUT_FOR_TRACKING_EVENT_IN_MS);
  }, [data, track]);

  const match = data?.findMatchForJobPosition;

  return (
    <>
      {shouldAddBreacrumbJsonIdSchema && <Breadcrumb items={breadcrumbItems} showBreadcrumb={false} />}
      <JobDetailsSeoTags
        match={match}
        jobPosition={jobPosition}
        isAnonymized={isAnonymized}
        relatedJobPositions={relatedJobPositions}
        asPath={jobDetailsUrl}
      />
      {!isAnonymized && (
        <JobDetailsJobPostingJsonLd
          match={match}
          jobPosition={jobPosition}
          isAnonymized={isAnonymized}
          validThrough={validThrough}
          relatedJobPositions={relatedJobPositions}
        />
      )}
      <JobPositionIdContextProvider value={jobPosition.id}>
        <JobDetail
          match={match}
          matchLoading={matchLoading}
          matchReasonsIsTest={matchReasonsIsTest}
          // @ts-expect-error Conflict with custom vs generated type
          jobPosition={jobPosition}
          highlights={highlights}
          perks={perks}
          validThrough={validThrough}
          // @ts-expect-error Conflict with custom vs generated type
          relatedJobPositions={relatedJobPositions}
          alternativeJobTitlesByPriority={alternativeJobTitlesByPriority}
          nearbyCities={nearbyCities}
        />
      </JobPositionIdContextProvider>
    </>
  );
};

export default JobDetailBase;
